// components/daimon/DaimonVisualCard.tsx
'use client';

// SHE SHOWS WHILE SHE TALKS (owner, 2026-10-07, after ChatGPT's Intelligent UI). A brand's live data
// beside the Daimon's words: the same store map, openings and revenue charts and shop photos as the
// brand page, drawn from the same records, so what she says and what the user sees never differ.
// The heavy views load only when a card asks for them.
import React, { useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { BrandMark } from '@/components/ui/BrandMark';
import type { DaimonConversationMessage, LeasingBrandDTO } from '#types/types';
import type { StoreLocation } from '@/components/portal/StoresMapPanel';

type VisualCard = Extract<NonNullable<DaimonConversationMessage['cards']>[number], { kind: 'visual' }>;

const Loading = () => <div className="skeleton h-full min-h-[220px] w-full rounded-[12px]" aria-busy="true" />;
const StoresMapPanel = dynamic(() => import('@/components/portal/StoresMapPanel').then((m) => m.StoresMapPanel), { ssr: false, loading: Loading });
const BrandExpansionChart = dynamic(() => import('@/components/portal/BrandExpansionChart').then((m) => m.BrandExpansionChart), { ssr: false, loading: Loading });
const BrandRevenueChart = dynamic(() => import('@/components/portal/BrandRevenueChart').then((m) => m.BrandRevenueChart), { ssr: false, loading: Loading });
const BrandOverviewHero = dynamic(() => import('@/components/portal/BrandStoreGallery').then((m) => m.BrandOverviewHero), { ssr: false, loading: Loading });

export const DaimonVisualCard = ({ card, lang }: { card: VisualCard; lang: 'en' | 'fr' }) => {
  const { kernel } = useKernel();
  const brand = useKernelStore((state) => state.inventory.Brand.registry[card.brandId] as LeasingBrandDTO | undefined);
  // The brand page's own detail read: locations, financials and openings land in the registry.
  useEffect(() => { void kernel.current?.syncEntity('Brand', card.brandId); }, [kernel, card.brandId]);
  const open = () => { void kernel.current?.focusEntityUI('Brand', card.brandId); };
  const fr = lang === 'fr';
  return (
    <section aria-label={`${card.title} · ${card.subtitle}`} className="w-full max-w-[640px] overflow-hidden rounded-[20px] border border-[#262626] bg-[#1B1B1B]">
      <header className="flex items-center gap-3 px-4 pb-2 pt-3">
        {brand?.logoUrl ? <BrandMark logoUrl={brand.logoUrl} name={card.title} size={28} radius={8} /> : null}
        <span className="min-w-0 flex-1 truncate">
          <span className="font-medium text-[#ECECEC]" style={{ fontSize: 14 }}>{card.title}</span>
          <span className="text-[#8E8E8E]" style={{ fontSize: 13 }}> · {card.subtitle}</span>
        </span>
        <button type="button" onClick={open} className="h-8 shrink-0 rounded-full px-3 text-[#AFAFAF] transition-colors hover:bg-white/[0.08] hover:text-white" style={{ fontSize: 13 }}>
          {fr ? 'Ouvrir la fiche' : 'Open brand'}
        </button>
      </header>
      <div className="px-3 pb-3">
        {card.visual === 'stores' ? (
          <div className="relative h-[340px] overflow-hidden rounded-[12px]">
            <div className="absolute inset-0">
              {brand?.locations ? (
                <StoresMapPanel locations={brand.locations as StoreLocation[]} brandName={brand.name} brandLogoUrl={brand.logoUrl} lang={lang} brandId={card.brandId} />
              ) : <Loading />}
            </div>
          </div>
        ) : card.visual === 'openings' ? (
          <BrandExpansionChart brandId={card.brandId} lang={lang} />
        ) : card.visual === 'revenue' ? (
          <BrandRevenueChart brandId={card.brandId} lang={lang} />
        ) : (
          <div className="space-y-3">
            <BrandOverviewHero name={card.title} logoUrl={brand?.logoUrl} photos={brand?.storeGallery} onOpenGallery={open} lang={lang} />
            {brand?.description ? <p className="px-1 text-[#AFAFAF]" style={{ fontSize: 13, lineHeight: '19px' }}>{brand.description}</p> : null}
          </div>
        )}
      </div>
    </section>
  );
};
